import { createRouter, createWebHistory, isNavigationFailure, RouteRecordRaw } from 'vue-router';
import { Actions, Subjects } from '@boqnet/permissions';
import i18n from '@/plugins/vue-i18n';
import { isAuthenticated } from '@/middleware/auth';
import { useLoaderStore } from '@/stores/utils/loader.store';
import AuthRoutes from '@/router/auth.routes';
import ProjectRoutes from '@/router/project.routes';
import SettingsRoutes from '@/router/settings.routes';
import BoqAdminRoutes from '@/router/boq-admin.routes';
import VendorsRoutes from '@/router/vendors.routes';
import AccountsRoutes from '@/router/accounts.routes';
import { useRouteHistoryStore } from '@/stores/utils/route-history.store';
import OrdersRoutes from '@/router/orders.routes';
import ClientsRoutes from '@/router/clients.routes';
import { selectedOrgId } from '@/common/utils/role.utils';
import { checkAndSetOrganisation } from '@/common/utils/user.utils';
import { analyticsService } from '@/plugins/post-hog';
import { isPostHogEnabled } from '@/utils/flags';
import InternalRoutes from '@/router/internal.routes';
import { usePermissionStore } from '@/stores/permission.store';
import { useAuthStore } from '@/stores/auth.store';
import { isRouteBlockedByAbility } from '@/common/utils/route-permission.utils';
import { refusedRouteRedirect } from '@/common/utils/landing.utils';
import { ownsPendingOnboarding, pendingOnboardingEmail } from '@/common/utils/onboarding.utils';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    layout?: string;
    requiresAuth?: boolean;
    /** CASL action required to access this route */
    action?: Actions;
    /** CASL subject required to access this route */
    subject?: Subjects;
    /**
     * OR-based permission check — the user must satisfy at least one entry.
     * Takes precedence over the single action/subject pair when present.
     */
    anyOf?: Array<{ action: Actions; subject: Subjects }>;
    /**
     * Why this route deliberately needs no ability check — an unauthenticated page, an
     * error/state page the guard itself redirects to, or an internal example with no data.
     *
     * Context: `isRouteBlockedByAbility` treats absent meta as ALLOW, so an undeclared route
     * is an open route. This makes the difference between "considered and open" and "nobody
     * has looked" visible, and `route-ability-meta.test.ts` fails on a route that has neither
     * this nor a guard (PLE-1239).
     */
    unguarded?: string;
  }
}

const { t } = i18n.global;

/**
 * Exported so `route-ability-meta.test.ts` can walk the whole tree and assert that every
 * navigable route either declares an ability guard or says in `meta.unguarded` why it needs
 * none. The router itself is unaffected.
 */
export const routes: Array<RouteRecordRaw> = [
  {
    path: '/:organisationId?/dashboard',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      title: t('routes.dashboardPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORG_DASHBOARD,
    },
  },
  {
    path: '/:organisationId?/create-org',
    name: 'create-org',
    component: () => import('@/views/CreateOrganisation.vue'),
    meta: {
      title: t('routes.createOrgPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.CREATE,
      subject: Subjects.ORGANISATION,
    },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/maintenance/Maintenance.vue'),
    meta: {
      title: t('components.pages.maintenance.title'),
      layout: 'error',
      unguarded: 'Maintenance page — shown instead of the app, no org data',
    },
  },
  {
    path: '/no-user-orgs',
    name: 'no-user-orgs',
    component: () => import('@/views/NoOrgView.vue'),
    meta: {
      title: t('components.pages.maintenance.title'),
      layout: 'error',
      requiresAuth: true,
      unguarded: 'Terminal state for a user with no organisation',
    },
  },
  {
    path: '/:organisationId?/no-projects-or-sites',
    name: 'no-projects-or-sites',
    component: () => import('@/views/NoProjSiteView.vue'),
    meta: {
      title: t('components.pages.noProjectsOrSites.title'),
      layout: 'default',
      requiresAuth: true,
      unguarded: 'Empty-state page, no org data',
    },
  },
  {
    path: '/:organisationId?/no-permissions',
    name: 'no-permissions',
    component: () => import('@/views/NoPermissionsView.vue'),
    meta: {
      title: t('components.pages.noPermissionsToView.title'),
      layout: 'default',
      requiresAuth: true,
      // Guarding this would loop: the ability guard redirects here on denial.
      unguarded: 'The guard redirects here on denial',
    },
  },
  {
    path: '/:catchAll(.*)',
    name: 'notfound',
    component: () => import('@/views/error/NotFound.vue'),
    meta: {
      title: t('pageTitles.notFound'),
      layout: 'error',
      unguarded: '404 page',
    },
  },
  {
    path: '/server-error',
    name: 'serverError',
    component: () => import('@/views/error/Server.vue'),
    meta: {
      title: t('pageTitles.serverError'),
      layout: 'error',
      unguarded: 'Error page',
    },
  },
  {
    path: '/base-layout',
    name: 'base-layout',
    component: () => import('@/views/BaseLayoutExampleView.vue'),
    meta: {
      title: 'Base Layout Example',
      layout: 'navigationOnly',
      requiresAuth: true,
      unguarded: 'Internal layout example, renders no org data',
    },
  },
  {
    path: '/base-layout-tabs',
    name: 'base-layout-tabs',
    component: () => import('@/views/BaseLayoutExampleViewWithTabs.vue'),
    meta: {
      title: 'Base Layout Example With Tabs',
      layout: 'navigationOnly',
      requiresAuth: true,
      unguarded: 'Internal layout example, renders no org data',
    },
  },
  ...AuthRoutes,
  ...VendorsRoutes,
  ...ProjectRoutes,
  ...SettingsRoutes,
  ...BoqAdminRoutes,
  ...AccountsRoutes,
  ...OrdersRoutes,
  ...ClientsRoutes,
  ...InternalRoutes,
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,

  scrollBehavior() {
    const layoutId = document.querySelector('#layout-container');

    if (layoutId) {
      layoutId.scrollTop = 0;
    }
  },
});

let appJustLoaded = true;

router.beforeEach(async (to, from, next) => {
  const permissionStore = usePermissionStore();

  useLoaderStore().start('navigate');
  const isMaintenanceMode = import.meta.env.VITE_APP_MAINTENANCE_MODE === 'true';

  const routeHistory = useRouteHistoryStore();

  routeHistory.setPreviousRoute(from.path);

  await checkAndSetOrganisation(to);

  permissionStore.setActiveProject(typeof to.params.id === 'string' ? to.params.id : '');
  permissionStore.setActiveSite(typeof to.params.siteId === 'string' ? to.params.siteId : '');

  // When the app first loads, `from.name` will be undefined
  if (!from.name && appJustLoaded) {
    sessionStorage.setItem('pageLoadIsRefresh', 'true');
  } else {
    sessionStorage.setItem('pageLoadIsRefresh', 'false');
  }

  appJustLoaded = false;

  if (isMaintenanceMode && to.name !== 'maintenance') {
    return next({ name: 'maintenance' });
  } else if (!isMaintenanceMode && to.name === 'maintenance') {
    return next({
      name: 'projects',
      params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
    });
  }

  // Context: post-TCH-469 a freshly self-registered user runs org-setup onboarding on their
  // first authenticated load, because registration no longer auto-signs-them-in and runs it
  // inline. The marker is set at registration (localStorage same-origin, cookie for the unified
  // hand-off from the auth SPA) and cleared by authStore.completeOnboarding() on the way out of
  // /account-setup, not here, so an abandoned setup is resumed rather than lost. It carries the
  // registering address, so a marker left behind on a shared browser only routes its own user.
  // The ownership check comes last on purpose: isAuthenticated() is what hydrates the user on
  // the unified-auth hand-off, so its email is only readable once that has run.
  if (
    pendingOnboardingEmail() &&
    to.name !== 'account-setup' &&
    (await isAuthenticated()) &&
    ownsPendingOnboarding(useAuthStore().loggedInUser?.email)
  ) {
    return next('/account-setup');
  }

  if (to.matched.some((record) => record.meta.requiresAuth) && !(await isAuthenticated())) {
    const authStore = useAuthStore();
    if (authStore.unifiedAuthEnabled) {
      const authBaseUri = import.meta.env.VITE_APP_AUTH_BASE_URI as string;
      if (authBaseUri) {
        globalThis.location.href = authBaseUri;
        return;
      }
      console.warn(
        '[UnifiedAuth] erp_app_unified_login_v1 is enabled but VITE_APP_AUTH_BASE_URI is not configured. Falling back to local login.',
      );
    }
    localStorage.setItem('redirectPath', to.fullPath);
    return next(`?redirect=${to.fullPath}`);
  }

  if (to.matched.some((record) => record.meta.requiresAuth) && (await isAuthenticated())) {
    if (JSON.parse(localStorage.getItem('user') || 'null')?.emailConfirmed) {
      if (permissionStore.isInitialized && isRouteBlockedByAbility(to.meta)) {
        return next({
          ...refusedRouteRedirect(),
          params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
        });
      }

      return next();
    }
    return next('/account-setup');
  }

  if (to.name === 'login' && (await isAuthenticated())) {
    return next({
      name: 'projects',
      params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
    });
  }

  return next();
});

router.afterEach((to, _, failure) => {
  if (isPostHogEnabled && analyticsService.isCurrentUserConsented()) {
    analyticsService.capture('page_view', {
      page_name: to.name,
      path: to.path,
    });
  }

  if (!isNavigationFailure(failure)) {
    document.title = 'Budget Control | ' + to.meta.title || '';
  }
  setTimeout(() => {
    useLoaderStore().end('navigate');
  }, 250);
});

export default router;
